const BOT_REGEX = /bot|crawl|spider/i;

export function isBot() {
  return BOT_REGEX.test(navigator.userAgent);
}

export function stHotelsTest() {
  return false;
}

export function stHotelsRateEndpoint() {
  return "/wp-json/sthotels/v1/hotel/search";
}

function generateStHotelLink(hotelId, checkinDate, checkoutDate, rooms, adults, query, widgetName) {
  const checkIn = checkinDate.format('YYYY-MM-DD');
  const checkOut = checkoutDate.format('YYYY-MM-DD');
  query = encodeURIComponent(query || '');
  let pageStateIdParam = '';
  if (window.__stsharedTrackingObj && window.__stsharedTrackingObj.pageStateId) {
    pageStateIdParam = `&wp_page_state_id=${window.__stsharedTrackingObj.pageStateId}`;
  }
  const utmContentSpecifier = widgetName ? `:${widgetName}` : '';
  const utmContent = `meta-widget${utmContentSpecifier}`;
  const pathPrefix = '';

  return (
    `https://hotels.smartertravel.com${pathPrefix}/hotels/${hotelId}` +
    `?query=${query}&check_in=${checkIn}&check_out=${checkOut}&rooms=${rooms}&adults=${adults}&children=0` +
    `&utm_medium=referral&utm_source=oyster${pageStateIdParam}&utm_content=${utmContent}`
  );
}

function getLogoUrl() {
  return 'https://images.hopjump.com/image/upload/c_scale,w_225/smartertravel/smarter_travel_color.svg'
}

function calculateDisplayPriceInt({ numNights, numRooms, hotel } = {}) {
  return perNightPriceLessTax({ numNights, numRooms, hotel });
}

function formatDisplayPrice(displayPriceInt) {
  if (!displayPriceInt) {
    return '';
  }
  return '$' + displayPriceInt.toFixed(0);
}

function perNightPriceLessTax({ numNights, numRooms, hotel } = {}) {
  return calculatePerNightPrice({ numNights, numRooms, price: priceLessTaxes(hotel) });
}

function priceLessTaxes(hotel) {
  if (!hotel.min_package_price_levels || !hotel.min_package_tax_levels) {
    return null;
  }

  if (!hotel.min_package_tax_levels.sell_price) {
    const BOSTON_AVERAGE_TAX_RATE = 0.116;
    return hotel.min_package_price_levels.sell_price * (1 - BOSTON_AVERAGE_TAX_RATE);
  }

  return hotel.min_package_price_levels.sell_price - hotel.min_package_tax_levels.sell_price;
}

function calculatePerNightPrice({ numNights, numRooms, price }) {
  if (!numNights || numNights < 1 || !numRooms || numRooms < 1) {
    return null;
  }

  if (!price) return null;

  return price / (numNights * numRooms);
}

// MAX_CHILDREN is 3 right now because the total number of people that
// can be in one room is 4 and one of them has to be an adult.
const MAX_CHILDREN = 3;
const MAX_PAX = 4;
const DEFAULT_CHILDRENS_AGE = 12;

const fillRoom = (rooms, adults, children = 0) => {
  if (!rooms && !adults && !children) {
    return undefined;
  }
  const childrenAges = Array(children).fill(DEFAULT_CHILDRENS_AGE);

  // Rooms is always going to win if we have it. If we don't we're going to calculate the least number of rooms possible
  const finalRoomCount =
    rooms ||
    Math.max(Math.ceil(((adults || 0) + (children || 0)) / MAX_PAX), Math.ceil((children || 0) / MAX_CHILDREN));

  // If the user hasn't specified how many adults there are, assume MAX_CHILDREN if only one room, or as many rooms as
  // there are.
  let adultsLeft = adults || (finalRoomCount === 1 && !children ? MAX_CHILDREN : finalRoomCount);
  let childrenLeft = children || 0;

  const roomList = [];
  for (let i = 0; i < finalRoomCount; i += 1) {
    // Fill up children first, since they require adults
    const numChildren = Math.max(0, Math.min(MAX_CHILDREN, childrenLeft));
    childrenLeft -= numChildren;
    // Each room requires one adult (including the rooms left.)
    const roomsLeft = finalRoomCount - (i + 1);
    const numAdults = Math.max(1, Math.min(MAX_PAX - numChildren, adultsLeft - roomsLeft));
    adultsLeft -= numAdults;

    roomList.push({
      adults: numAdults,
      children: childrenAges.slice(0, numChildren),
    });
  }

  return roomList;
};

export function requestBody({ gimmonixHotelIds, checkinDate, checkoutDate, rooms, adults }) {
  const checkIn = checkinDate.format('YYYYMMDD');
  const checkOut = checkoutDate.format('YYYYMMDD');
  return {
    hopjump_user_id: "2666914733533268992",
    request_parameters: {
      check_in: checkIn,
      check_out: checkOut,
      rooms: fillRoom(rooms, adults),
      hotel_ids: gimmonixHotelIds.filter(id => id),
      search_attributes: "MOD"
    },
    timeout: 10,
    requested_fields: [
      "display_name",
      "search_distance",
      "star_rating",
      "city_name",
      "state",
      "country_code",
      "last_booked",
      "times_booked_in_week",
      "address",
      "thumb_url",
      "hotel_facilities",
      "average_rating",
      "number_of_reviews",
      "trustyou_id",
      "chain_code",
      "images"
    ]
  };
}

/**
 *
 * It's possible that some of the requested hotels are not in the result.
 * We put in empty values for them.
 */
export function fillInMIAHotels(requestedHotelIds, response) {
  if (Array.isArray(response.results)) {
    const taIdsReceived = {};
    response.results.forEach(h => {
      taIdsReceived[h.id] = true
    });
    requestedHotelIds.forEach((id) => {
      if (!taIdsReceived[id]) {
        response.results.push({ id, hacOffers: { offers: [] } });
      }
    });
  }
}

export function restructureRatesResponse(response, fetchArgs, gimmonixIdToHotelMap) {
  const { taHotelIds, checkinDate, checkoutDate, rooms, adults } = fetchArgs;
  const numNights = checkoutDate.diff(checkinDate, 'days');

  const resturcturedResponse = {
    id: response.session_id,
    isGimmonix: true,
    status: { isComplete: true },
    unsupportedHotelIds: [],
    invalidHotelIds: [],
    results: response.payload
      .map((hotel) => {
        const { taId, cityAddress, widgetName } = gimmonixIdToHotelMap[`${hotel.hotel_id}`];

        const displayPriceInt = calculateDisplayPriceInt({ numNights, numRooms: rooms, hotel });
        const offer = {
          availability: 'available',
          displayName: 'SmarterTravel',
          internalName: 'SmarterTravel',
          displayPriceInt,
          displayPrice: formatDisplayPrice(displayPriceInt),
          link: generateStHotelLink(hotel.hotel_id, checkinDate, checkoutDate, rooms, adults, cityAddress, widgetName),
          logo: getLogoUrl(),
        };

        return {
          id: taId,
          isGimmonix: true,
          hacOffers: {
            availability: 'available',
            offers: [offer],
          }
        }
      }),
  };

  fillInMIAHotels(taHotelIds, resturcturedResponse);

  return resturcturedResponse;
}

export function mergeResponses(newResponse) {
  if (!stHotelsTest()) {
    return newResponse;
  }

  const { isGimmonix } = newResponse;
  const { gimmonix, ta, merged } = window.StSharedStHotelsRateUtils.results;

  newResponse.results.forEach(h => {
    // Sort the offers by price
    // This make unavailable offers go after available offers
    if (h.hacOffers.offers.length > 0) {
      h.hacOffers.offers.sort(
        (o1, o2) =>
          (o1.displayPriceInt || Number.POSITIVE_INFINITY) - (o2.displayPriceInt || Number.POSITIVE_INFINITY)
      );
    }

    // Add each hotel to their corresponding cache
    if (h.hacOffers.offers && h.hacOffers.offers.length > 0) {
      (isGimmonix ? gimmonix : ta)[h.id] = h.hacOffers.offers;
    }
  });

  // Get the merged offers for each result hotel
  newResponse.results.forEach(h => {
    const gimmonixOffers = gimmonix[h.id];
    const taOffers = ta[h.id];

    let mergedOffers = [...(gimmonixOffers || []), ...(taOffers || [])];

    merged[h.id] = mergedOffers;
    h.hacOffers.offers = mergedOffers;
  });

  return newResponse;
}

/**
 *
 * @param {*} hotelIds TA hotel ids
 * @returns
 */
export function clearResponseCache(hotelIds) {
  const { gimmonix, ta, merged } = window.StSharedStHotelsRateUtils.results;
  hotelIds.forEach((taId) => {
    delete gimmonix[taId];
    delete ta[taId];
    delete merged[taId];
  });
}

export function startRequests(taIds, isGimmonix) {
  taIds.forEach((id) => {
    if (isGimmonix) {
      window.StSharedStHotelsRateUtils.requests.gimmonix[id] = 'started';
    } else {
      window.StSharedStHotelsRateUtils.requests.ta[id] = 'started';
    }
  });
}

export function completeRequests(taIds, isGimmonix) {
  taIds.forEach((id) => {
    if (isGimmonix) {
      window.StSharedStHotelsRateUtils.requests.gimmonix[id] = 'complete';
    } else {
      window.StSharedStHotelsRateUtils.requests.ta[id] = 'complete';
    }
  });
}

export function isRequestComplete(taId, hasGimmonixId) {
  const { gimmonix, ta } = window.StSharedStHotelsRateUtils.requests;
  if (stHotelsTest() && hasGimmonixId) {
    return ta[taId] === 'complete' && gimmonix[taId] === 'complete';
  }
  return ta[taId] === 'complete';
}

// Set global var
if (!window.StSharedStHotelsRateUtils) {
  window.StSharedStHotelsRateUtils = {
    isBot,
    stHotelsTest,
    stHotelsRateEndpoint,
    requestBody,
    fillInMIAHotels,
    restructureRatesResponse,
    mergeResponses,
    clearResponseCache,
    startRequests,
    completeRequests,
    isRequestComplete,
    // track status of requests:
    // [ta_id] =>
    //   undefined = no action
    //   'started' = requested started
    //   'complete' = request completed
    requests: {
      gimmonix: {},
      ta: {},
    },
    // [ta_id] => offers[]
    results: {
      gimmonix: {},
      ta: {},
      merged: {},
    },
  }
}
